import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import type { CloseReview } from "@/lib/api";
import { dateLabel, monthLabel } from "./cultivation-dates";
import {
  closeStatus,
  evidenceCoverage,
  profileLabel,
  statusLabel,
  statusTone,
} from "./cultivation-readiness";
import type { CultivationWorkspace } from "./cultivation-types";
import { CultivationCapabilityNotice } from "./CultivationCapabilityNotice";
import { capabilityAllowed } from "./cultivation-capabilities";
import { CultivationPack } from "./CultivationPack";

const SETTINGS_LOCK_SEARCH = { tab: "books", section: "period-locks" } as const;

function Card({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="surface grid gap-3 rounded-2xl border border-[var(--border)] p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        {action ? <span className="ms-auto">{action}</span> : null}
      </div>
      {children}
    </section>
  );
}

function reviewFor(workspace: CultivationWorkspace): CloseReview | null {
  if (!workspace.installation) return null;
  return workspace.reviews.find((review) => review.installation_id === workspace.installation!.id) ?? null;
}

export function CultivationOverview({
  workspace,
  onOpenImports,
  onOpenReview,
}: {
  workspace: CultivationWorkspace;
  onOpenImports: () => void;
  onOpenReview: () => void;
}) {
  const review = reviewFor(workspace);
  const coverage = evidenceCoverage(
    workspace.imports.imports,
    workspace.licenseId,
    workspace.periodStart,
    workspace.periodEnd,
    workspace.imports.attestations,
  );
  const status = closeStatus(review, coverage);
  const posted = workspace.postings.filter((posting) => posting.status === "posted" || posting.status === "applied");
  const licensedImports = workspace.imports.imports.filter((item) => item.license_id === workspace.licenseId);
  const lastImport = licensedImports[0] ?? null;
  const canImport = capabilityAllowed(workspace.capabilities, "import");
  const canReview = capabilityAllowed(workspace.capabilities, "review");

  return (
    <div className="grid gap-5">
      <section className="surface grid gap-4 rounded-2xl border border-[var(--border)] p-5">
        <div className="flex flex-wrap items-start gap-3">
          <div>
            <p className="text-sm font-medium">{monthLabel(workspace.periodStart)}–{monthLabel(workspace.periodEnd)} close</p>
            <p className="mt-1 text-sm text-[var(--muted-foreground)]">
              {workspace.licenseId
                ? "Evidence, inputs, and review for the selected location."
                : "Choose a location to begin the close."}
            </p>
          </div>
          <Badge className="ms-auto" tone={statusTone(status)}>{statusLabel(status)}</Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={onOpenImports} disabled={!workspace.licenseId || !canImport}>
            {coverage.ready ? "Review imports" : "Import reports"}
          </Button>
          <Button type="button" variant="secondary" onClick={onOpenReview} disabled={!workspace.licenseId || !canReview}>
            {review ? "Continue close review" : "Start close review"}
          </Button>
          <Button asChild variant="outline">
            <Link to="/settings" search={SETTINGS_LOCK_SEARCH}>Post and lock in Settings</Link>
          </Button>
        </div>
        {!workspace.installation ? (
          <p role="alert" className="text-sm text-[var(--danger)]">
            The cultivation costing extension is not installed or enabled for this organization.
          </p>
        ) : null}
        {!canImport ? <CultivationCapabilityNotice capability="import" action="import or replace METRC evidence" /> : null}
        {!canReview ? <CultivationCapabilityNotice capability="review" action="open and save the close review" /> : null}
        {workspace.capabilitiesError ? (
          <p className="text-xs text-[var(--muted-foreground)]">
            Cultivation capabilities are not available right now: {workspace.capabilitiesError}. Actions remain server-checked.
          </p>
        ) : null}
      </section>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card
          title="Evidence"
          action={<Badge tone={coverage.ready ? "success" : "warning"}>{coverage.ready ? "Ready" : `${coverage.missing.length} missing`}</Badge>}
        >
          {coverage.ready ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              Every required monthly report is present for the selected period.
            </p>
          ) : (
            <ul className="grid gap-1 text-sm text-[var(--muted-foreground)]">
              {coverage.missing.slice(0, 6).map((cell) => (
                <li key={`${cell.profile}-${cell.month}`}>
                  Missing {profileLabel(cell.profile)} for {monthLabel(cell.month)}
                </li>
              ))}
              {coverage.missing.length > 6 ? <li>…and {coverage.missing.length - 6} more</li> : null}
            </ul>
          )}
          <p className="text-xs text-[var(--muted-foreground)]">
            {lastImport ? `Last import ${lastImport.file_name} · ${dateLabel(lastImport.created_at.slice(0, 10))}` : "No reports imported yet."}
          </p>
        </Card>

        <Card title="Close review" action={<Badge tone={statusTone(status)}>{statusLabel(status)}</Badge>}>
          <p className="text-sm text-[var(--muted-foreground)]">
            {review
              ? `Saved ${dateLabel(review.reviewed_at.slice(0, 10))}${review.last_error ? ` · ${review.last_error}` : ""}`
              : "No reviewed inputs are saved for this location and period."}
          </p>
          <p className="text-xs text-[var(--muted-foreground)]">
            Saving the review does not post journals or lock the period.
          </p>
        </Card>

        <Card title="Posting and lock" action={<Badge tone={posted.length > 0 ? "success" : "neutral"}>{posted.length} posted</Badge>}>
          <p className="text-sm text-[var(--muted-foreground)]">
            {posted.length > 0
              ? "Reviewed journals are posted. Confirm the entries, then lock the period in Settings."
              : "Post reviewed journals and lock the period after the review is saved."}
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to="/settings" search={SETTINGS_LOCK_SEARCH}>Open period locks</Link>
          </Button>
        </Card>
      </div>

      {posted.length > 0 ? <CultivationPack workspace={workspace} /> : null}

      <Card title="Recent imports">
        {licensedImports.length === 0 ? (
          <p className="text-sm text-[var(--muted-foreground)]">No METRC reports have been imported for this location.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Profile</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Period</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">File</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Rows</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Imported</th>
                </tr>
              </thead>
              <tbody>
                {licensedImports.slice(0, 6).map((item) => (
                  <tr key={item.source_document_id}>
                    <td className="px-2 py-1">{profileLabel(item.profile)}</td>
                    <td className="px-2 py-1">{item.period_start}–{item.period_end}</td>
                    <td className="px-2 py-1">{item.file_name}</td>
                    <td className="px-2 py-1">{item.row_count}</td>
                    <td className="px-2 py-1">{dateLabel(item.created_at.slice(0, 10))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
